import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { filter, skip, take } from 'rxjs/operators';
import { MedProDataService } from '../../data-services/medpro/medpro.data.service';
import { ContactDataService } from '../../data-services/contact/contact.data.service';
import { ContactOfflineService } from '../contact/contact.service';
import { DeviceService } from '../device/device.service';
import { EventsService } from '../events/events.service';
import { BulkAddOutcome, MedProHcp } from '../../data-services/medpro/medpro.models';

export type RefreshContactResult =
  | { ok: true; contextChanged?: boolean }
  | { ok: false; fallback: 'not-found'; errorMessage: string }
  | { ok: false; fallback: 'error'; errorMessage: string }
  | { ok: false; fallback: 'network-lost'; errorMessage: string };

export interface AddSelectedResult {
  outcome: BulkAddOutcome;
  /** Dynamics contactIds resolved from the MedPro master IDs of succeeded HCPs.
   *  Empty if the resolve query failed or none of the succeeded HCPs had a
   *  master ID. The caller uses these to run the post-add side effects
   *  (seed single contact, licenses, consents, updateContactLists). */
  contactIds: string[];
}

type OperationOutcome<T> =
  | { state: 'ok'; value: T }
  | { state: 'offline-mid-flight' }
  | { state: 'error'; error: any };

@Injectable({ providedIn: 'root' })
export class MedProSearchService {
  readonly progress$ = new BehaviorSubject<{ current: number; total: number } | null>(null);

  constructor(
    private medProData: MedProDataService,
    private contactData: ContactDataService,
    private contactService: ContactOfflineService,
    private device: DeviceService,
    private events: EventsService,
  ) {}

  async addSelected(hcps: MedProHcp[]): Promise<AddSelectedResult> {
    const outcome: BulkAddOutcome = { succeeded: [], failed: [] };
    for (let i = 0; i < hcps.length; i++) {
      this.progress$.next({ current: i + 1, total: hcps.length });
      const r = await this.runWithGuards(
        () => this.medProData.addHcpFromMedpro(hcps[i].indskr_medprohcpid),
      );
      if (r.state === 'ok') {
        outcome.succeeded.push(hcps[i]);
      } else if (r.state === 'offline-mid-flight') {
        outcome.failed.push({
          hcp: hcps[i],
          errorMessage: 'Network connection lost',
          networkLost: true,
        });
      } else {
        outcome.failed.push({ hcp: hcps[i], errorMessage: this.extractMessage(r.error) });
      }
    }
    this.progress$.next(null);

    let contactIds: string[] = [];
    if (outcome.succeeded.length > 0) {
      const masterIds = outcome.succeeded
        .map(h => h.indskr_medpromasterid)
        .filter((m): m is number => m != null);
      try {
        contactIds = await this.medProData.findContactIdsByMasterIds(masterIds);
      } catch (e) {
        console.warn('MedPro: failed to resolve master IDs to contactIds', e);
      }
    }
    return { outcome, contactIds };
  }

  /** Refresh an existing contact's MedPro fields and pull the fresh copy
   *  locally so contactInformationObservable fires. Uses the same refetch
   *  params (fullsync, skip-linked-delta) that postContactToPosition uses
   *  internally for global-search Add. */
  async refreshContactFromMedpro(
    contactId: string,
    medProHcpId?: string,
  ): Promise<RefreshContactResult> {
    const sync = await this.runWithGuards(
      () => this.medProData.refreshContactFromMedpro(contactId, medProHcpId),
    );
    if (sync.state === 'offline-mid-flight') {
      return { ok: false, fallback: 'network-lost', errorMessage: 'Network connection lost' };
    }
    if (sync.state === 'error') {
      const msg = this.extractMessage(sync.error);
      if (/No matching MedPro HCP found/i.test(msg)) {
        return { ok: false, fallback: 'not-found', errorMessage: msg };
      }
      return { ok: false, fallback: 'error', errorMessage: msg };
    }

    const refetch = await this.runWithGuards(
      () => this.contactData.getContactDetailsByContactId(contactId, false, true),
    );
    const refreshed = refetch.state === 'ok' ? refetch.value : null;
    if (refetch.state === 'error') {
      console.warn('MedPro: refetch after update failed', refetch.error);
    }

    // getContactDetailsByContactId replaces contactService.contactInformation
    // with a fresh Contact instance ONLY when the user is still viewing the
    // same contact. If the user navigated away between click and resolve, the
    // observable's last emission targets a different contact — mutating it
    // here would apply MedPro details to the wrong record (OMNI-59618 #5).
    // So:
    //  - Always update contacts[] master cache so the list redraws the
    //    originating row with fresh data.
    //  - Always fire contactUpdated so contact-info re-inits when the user
    //    revisits the contact.
    //  - Only assign to contactInformation when stillSelected is true.
    const stillSelected = this.contactService.contactInformation?.ID === contactId;
    if (refreshed) {
      const idx = this.contactService.contacts.findIndex((c: any) => c.ID === contactId);
      if (idx >= 0) this.contactService.contacts[idx] = refreshed;
      if (stillSelected) this.contactService.contactInformation = refreshed;
    }
    this.contactService.contactUpdated.next(true);
    this.events.publish('contactSampleEligibilityUpdated', contactId);
    // The glance card / page-title in contact-details is initialized once per
    // contact-id change (contact-details.ts:276 guards on ID inequality), so a
    // same-id Revalidate doesn't refresh it. Fire the dedicated full-refresh
    // event so contact-details rebuilds name + address + primary values, not
    // just the buttons array (the 'PageTitleOnly' event is buttons-only).
    if (stillSelected) this.events.publish('refreshContactDetailGlanceData');
    return { ok: true, contextChanged: !stillSelected };
  }

  /** Race the work against any mid-flight transition to offline. The Dataverse
   *  client doesn't propagate AbortSignal so we can't truly cancel the network
   *  request — but the late resolve gets discarded, which is what user-visible
   *  cancellation needs. A slow-but-still-online call is allowed to finish; no
   *  arbitrary timeout, since MedPro round-trips can run 7-10s+ and a fixed
   *  cap creates false-negative toasts when the server actually succeeded. */
  private runWithGuards<T>(work: () => Promise<T>): Promise<OperationOutcome<T>> {
    return new Promise(resolve => {
      let settled = false;
      const settle = (out: OperationOutcome<T>) => {
        if (settled) return;
        settled = true;
        sub?.unsubscribe();
        resolve(out);
      };
      // skip(1) drops the BehaviorSubject's current value — we only care about
      // transitions during the call. filter+take stops the subscription after
      // the first offline=true emission.
      const sub = this.device.isOfflineObservable
        .pipe(skip(1), filter(off => !!off), take(1))
        .subscribe(() => settle({ state: 'offline-mid-flight' }));
      work()
        .then(value => settle({ state: 'ok', value }))
        .catch(error => settle({ state: 'error', error }));
    });
  }

  private extractMessage(err: any): string {
    if (!err) return 'Unknown error';
    if (typeof err === 'string') return err;
    return err.message || err.Message || String(err);
  }
}
